// @ts-check
import React from 'react';

import FullScreenError from 'components/molecules/FullScreenError';
import Actions from 'components/standard/HostingDetailsActions';
import Attributes from 'components/standard/HostingDetailsAttributes';
import PageLayout from 'components/standard/PageLayout';
import TabBar from 'components/standard/TabBar';
import { useLeftHandLayout } from 'containers/LeftHandLayout';
import hostingDetailsAlsowith from 'containers/pages/servers/details/hostingDetailsAlsowith';
import useAssetDetails from 'modules/queries/asset/useDetails';
import NotFound from 'pages/error/NotFound';
import {
	Redirect,
	Link as RouterLink,
	useLocation,
	useParams,
} from 'react-router-dom';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Tab from '@liquidweb-dev/undercurrent/Tab';
import Tabs from '@liquidweb-dev/undercurrent/Tabs';
import { getErrorStringHelper } from 'utility/redux/selectorHelperFunctions/apiModules';

/** @typedef {import('@liquidweb-dev/undercurrent').Theme['palette']} Palette */

/**
 * A layout for VMware.vCloud.vDC servers' details page.
 * @param {object} props
 * @param {React.ReactNode} props.children
 */
const VmWareVdcLayout = ({ children }) => {
	/** @type {{uniqId: string}} */
	const { uniqId } = useParams();
	const { pathname } = useLocation();
	const { topInnerX } = useLeftHandLayout();

	const {
		data: assetDetailsData,
		isLoading: assetDetailsIsLoading,
		isError: assetDetailsIsError,
		error: assetDetailsError,
	} = useAssetDetails({
		uniq_id: uniqId,
		alsowith: hostingDetailsAlsowith,
	});

	if (
		assetDetailsIsError &&
		// @ts-expect-error
		assetDetailsError.statusCode === 404
	) {
		return <NotFound />;
	}

	if (assetDetailsIsError) {
		return (
			<FullScreenError content={getErrorStringHelper(assetDetailsIsError)} />
		);
	}

	const { domain, isVmware } = assetDetailsData || {};

	// redirect non VMware.vCloud.vDC servers
	if (isVmware === false) {
		return <Redirect to={`/servers/${uniqId}`} />;
	}

	return (
		<PageLayout
			title={domain || ''}
			attributesSlot={<Attributes />}
			actionsSlot={<Actions />}
			isLoading={assetDetailsIsLoading}
		>
			<TabBar sx={{ marginBottom: 600 }} isLoading={assetDetailsIsLoading}>
				<Tabs value={pathname} variant="scrollable">
					<Tab
						component={RouterLink}
						value={`/servers/${uniqId}/vmware-vdc`}
						to={`/servers/${uniqId}/vmware-vdc`}
						label="Overview"
					/>
					<Tab
						component={RouterLink}
						value={`/servers/${uniqId}/vmware-vdc/networking`}
						to={`/servers/${uniqId}/vmware-vdc/networking`}
						label="Networking"
					/>
					<Tab
						component={RouterLink}
						value={`/servers/${uniqId}/vmware-vdc/activity`}
						to={`/servers/${uniqId}/vmware-vdc/activity`}
						label="Activity"
					/>
				</Tabs>
			</TabBar>
			<Stack sx={{ paddingX: { xs: `${topInnerX}px`, md: 0 }, flexGrow: 1 }}>
				{children}
			</Stack>
		</PageLayout>
	);
};

export default VmWareVdcLayout;
